import { Controller } from "@hotwired/stimulus"

// Fills the Add-user temporary password with a value the admin can actually hand over.
// Client-side on purpose: this is a blank field they were typing by hand, and minting
// one in the browser avoids a new endpoint for something that never needs to be stored
// until they submit the form.
//
// After generate the field becomes type=text. A password input would hide the only copy
// of a secret they still have to share out of band. Generating does not write the
// clipboard — unlike a reset link, this is still a form value they might replace.
export default class extends Controller {
  static targets = ["field", "copyButton"]

  static LENGTH = 16
  static LOWER = "abcdefghijkmnpqrstuvwxyz"
  static UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ"
  static DIGITS = "23456789"
  static SYMBOLS = "!@#$%&*?"

  static COPIED_LABEL = "Copied"
  static REST_LABEL = "Copy"
  static REVERT_DELAY = 2000

  disconnect() {
    clearTimeout(this.revertTimeout)
  }

  generate() {
    this.fieldTarget.value = this.buildPassword()
    this.fieldTarget.type = "text"
    this.selectField()
    this.reportAtRest()
  }

  copy() {
    const password = this.fieldTarget.value
    if (!password) return

    this.selectField()
    if (!navigator.clipboard?.writeText) return this.reportUnavailable()

    navigator.clipboard.writeText(password)
      .then(() => this.reportCopied())
      .catch(() => this.reportUnavailable())
  }

  buildPassword() {
    const groups = [
      this.constructor.LOWER,
      this.constructor.UPPER,
      this.constructor.DIGITS,
      this.constructor.SYMBOLS
    ]
    const alphabet = groups.join("")
    const chars = groups.map((group) => this.pick(group))

    while (chars.length < this.constructor.LENGTH) {
      chars.push(this.pick(alphabet))
    }

    return this.shuffle(chars).join("")
  }

  pick(alphabet) {
    return alphabet[this.randomIndex(alphabet.length)]
  }

  shuffle(chars) {
    for (let index = chars.length - 1; index > 0; index -= 1) {
      const swapWith = this.randomIndex(index + 1)
      ;[ chars[index], chars[swapWith] ] = [ chars[swapWith], chars[index] ]
    }
    return chars
  }

  randomIndex(limit) {
    const ceiling = Math.floor(0x100000000 / limit) * limit
    const bytes = new Uint32Array(1)
    let value

    do {
      crypto.getRandomValues(bytes)
      value = bytes[0]
    } while (value >= ceiling)

    return value % limit
  }

  selectField() {
    this.fieldTarget.focus()
    this.fieldTarget.select()
  }

  reportCopied() {
    this.copyButtonTarget.textContent = this.constructor.COPIED_LABEL
    this.copyButtonTarget.classList.add("is-on")

    clearTimeout(this.revertTimeout)
    this.revertTimeout = setTimeout(() => this.reportAtRest(), this.constructor.REVERT_DELAY)
  }

  reportUnavailable() {
    this.reportAtRest()
  }

  reportAtRest() {
    this.copyButtonTarget.textContent = this.constructor.REST_LABEL
    this.copyButtonTarget.classList.remove("is-on")
  }
}
